import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { FactsTableRow } from '../../components/FactsTable'
import { formatPercentage } from '../../../packages/utils/numberFormatters'

type DilutionRowComponent = React.FC<{
    issue: EnrichedIssueEntity
}>

type IssueDilutionRowComponent = React.FC<{
    issue: EnrichedIssueEntity
    locale: string
}>

const RightsIssueDiltionRow: IssueDilutionRowComponent = ({ issue, locale }) => {
    const {
        totalOfferedUnits,
        overAllotmentSize,
        overAllotmentAllocation,
        sharesInUnit,
        unitsReceived,
        rightsRequired,
        rightsReceived,
        sharesRequired,
        unitSubTotal,
    } = issue
    const totalOfferedUnitsInt = totalOfferedUnits ?? 0

    const maxDilutionValue =
        (100 * (totalOfferedUnitsInt + overAllotmentSize) * sharesInUnit) /
        ((totalOfferedUnitsInt + overAllotmentSize) * sharesInUnit +
            (totalOfferedUnitsInt / unitsReceived) * (rightsRequired / rightsReceived) * sharesRequired)

    const actualDilutionValue =
        (100 * (unitSubTotal + overAllotmentAllocation) * sharesInUnit) /
        ((unitSubTotal + overAllotmentAllocation) * sharesInUnit +
            (((totalOfferedUnitsInt / unitsReceived) * rightsRequired) / rightsReceived) * sharesRequired)

    if (!maxDilutionValue) {
        return null
    }

    return (
        <FactsTableRow label='Utspädning'>
            <Typography>{formatPercentage(maxDilutionValue, { locale, decimals: 0 })} (maximal)</Typography>
            {actualDilutionValue ? (
                <Typography>{formatPercentage(actualDilutionValue, { locale, decimals: 0 })} (faktisk)</Typography>
            ) : null}
        </FactsTableRow>
    )
}

const OtherIssueDilutionRow: IssueDilutionRowComponent = ({ issue, locale }) => {
    const { totalOfferedUnits, unitSubTotal, numberOfSharesBeforePublicationDate } = issue

    const numberOfNewShares = unitSubTotal !== null ? unitSubTotal : totalOfferedUnits

    const dilution = numberOfNewShares
        ? (numberOfNewShares * 100) / (numberOfNewShares + (numberOfSharesBeforePublicationDate || 0))
        : null

    return (
        <FactsTableRow label='Utspädning'>
            <Typography>
                {dilution ? formatPercentage(dilution, { locale, decimals: 0 }) : 'Antal nya aktier saknas'}
            </Typography>
        </FactsTableRow>
    )
}

export const DilutionRow: DilutionRowComponent = ({ issue }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    if (issue.type === 'Rights issue') {
        return <RightsIssueDiltionRow issue={issue} locale={locale} />
    }

    return <OtherIssueDilutionRow issue={issue} locale={locale} />
}
